/**
 * APPROVALS_GATE_V1: a company's `approvals.always` held by the system on the connected-app path (the #4367
 * audit's fix, part 2a). Before it, an app action in a listed class waited only if the model chose to ask.
 *
 * Every Composio action a call runs is read from structure, never from words: the call's own slug, and each item
 * of a MULTI_EXECUTE (the strictest wins, so one listed action holds the whole batch). A workbench script or remote
 * shell command runs no app action: Composio's remote sandbox cannot call an app. Each slug is then looked up by exact membership
 * (approval-catalog.ts): in a class, reviewed as in none, trusted read-only by its vendor (composioVerdict's
 * readonly, which already distrusts a read-only claim on a floor verb), or unknown. The call is held when one of
 * its actions is in a class the version lists, or when any is unknown and the version lists anything: what no
 * catalog knows waits for the yes.
 *
 * The browser path (2b) is read by the box before the action (sandbox browser/approval-facts.ts), from structure and,
 * for payment only, the control's own label: an action pays when it submits a form holding payment fields, on a page
 * with a payment processor's frame, acts on a processor's own page, or lands on a control whose own label says it pays
 * ("Pay ₹4,820", "Place order"; agent wallet P2 task 9); it commits when it submits a form by a method other than GET
 * (a login form excepted). The classes a browser commit is in cannot be told apart, so a commit is held for any of
 * booking, cancellation, send_email, publish and delete. An action that can commit whose facts the box could not read (or one whose effect
 * no reading proves, such as running script or uploading a file) is unknown.
 *
 * A direct call's registered name is cut to 64 characters, so a slug over 49 reaches the gate truncated: the box
 * hands the gate the full slug it registered (`composioSlug`), so the catalog and the vendor's read-only hint see it.
 *
 * decideToolGate step 5c returns the unsilenceable ask for a held call, like a company action (step 5b): no mode,
 * allow rule or hook silences it, and on a run with no person present the card is held until someone answers it,
 * never run without one. The box sets `approvals` only for a company task (or a teammate or helper of one) with the
 * flag on, so every other call is byte-identical. PURE / TOTAL.
 */
import type { ToolCall } from '../contract/harness.js';
import { COMPOSIO_TOOL_PREFIX } from './engine-constants.js';
import { extractInnerComposioTools, isComposioExecutorMetaTool, isComposioMetaTool, isComposioRemoteCodeTool } from './composio-naming.js';
import { composioVerdict, type ComposioToolHints } from './sensitivity-floor.js';
import { approvalClassesOfSlug, REVIEWED_NO_CLASS, type ApprovalClass } from './approval-catalog.js';

/** What the box read about a browser action before it runs (sandbox browser/approval-facts.ts). */
export interface BrowserApprovalFacts {
  /**
   * It submits a form with payment fields or on a page with a payment processor's frame, acts on a processor's page,
   * or lands on a control whose own label says it pays (sandbox payment-hosts.ts PAY_INTENT_OF_LABEL).
   */
  payment: boolean;
  /** It submits a form by a method other than GET, a login form excepted. */
  commit: boolean;
}

const BROWSER_PREFIX = 'mcp__browser-control__';

/** The browser actions that can commit something; every other browser tool reads, scrolls or navigates (a GET). */
export const BROWSER_COMMIT_CAPABLE_TOOLS: ReadonlySet<string> = new Set([
  'computer_click', 'computer_type', 'computer_key', 'computer_select', 'computer_dialog', 'computer_eval', 'computer_upload',
  'computer_drag',
]);

/** Classes a browser commit may be in: the structure of a form cannot tell a booking from a send. */
const COMMIT_CLASSES: readonly ApprovalClass[] = ['booking', 'cancellation', 'send_email', 'publish', 'delete'];

/** What the box hands the gate for a company task's approvals (sandbox policy-port.impl.ts). */
export interface ApprovalContext {
  /** The version's approvals.always: never empty (an empty list hands nothing). */
  listed: ReadonlySet<string>;
  /** A direct Composio call's full slug, when its registered name was cut to 64 characters. */
  composioSlug?: string;
  /** What the box read about a browser action that can commit (2b); absent for any other call, or when it could not read. */
  browser?: BrowserApprovalFacts;
}

export interface ApprovalVerdict {
  /** The approval classes the call's actions are catalogued in. */
  classes: ApprovalClass[];
  /** The actions no catalog knows, by slug (an unreadable batch by its own name). */
  unknown: string[];
}

/** What a call's connected-app actions are, for the approvals it may wait on; null when it runs none. */
export function approvalVerdict(
  call: ToolCall,
  hints?: ComposioToolHints,
  composioSlug?: string,
  browser?: BrowserApprovalFacts,
): ApprovalVerdict | null {
  const name = typeof call?.toolName === 'string' ? call.toolName : '';
  if (name.startsWith(BROWSER_PREFIX)) {
    const tool = name.slice(BROWSER_PREFIX.length);
    if (!BROWSER_COMMIT_CAPABLE_TOOLS.has(tool)) return null;
    if (browser === undefined) return { classes: [], unknown: [tool] };
    return { classes: [...(browser.payment ? (['payment'] as const) : []), ...(browser.commit ? COMMIT_CLASSES : [])], unknown: [] };
  }
  if (!name.startsWith(COMPOSIO_TOOL_PREFIX)) return null;
  // Code in Composio's remote sandbox calls no app; code that tries goes back to the agent first (decideToolGate 2c3).
  if (isComposioRemoteCodeTool(name)) return null;
  const own = (composioSlug ?? name.slice(COMPOSIO_TOOL_PREFIX.length)).toUpperCase();
  let slugs: string[];
  if (isComposioExecutorMetaTool(name)) {
    const inner = extractInnerComposioTools(call);
    if (inner === null) return { classes: [], unknown: [own] };
    slugs = inner.map((t) => t.slug);
  } else if (isComposioMetaTool(name)) {
    return null; // searching, connecting and checking a connection act on nothing
  } else {
    slugs = [own];
  }
  const classes = new Set<ApprovalClass>();
  const unknown: string[] = [];
  for (const slug of slugs) {
    const of = approvalClassesOfSlug(slug);
    for (const c of of) classes.add(c);
    if (of.length === 0 && !REVIEWED_NO_CLASS.has(slug) && composioVerdict(slug, hints) !== 'readonly') unknown.push(slug);
  }
  return { classes: [...classes], unknown };
}

/** Is this call held for the person's yes by the version's `approvals.always`? Absent or empty: never. */
export function approvalHolds(call: ToolCall, approvals: ApprovalContext | undefined, hints?: ComposioToolHints): boolean {
  if (approvals === undefined || approvals.listed.size === 0) return false;
  const verdict = approvalVerdict(call, hints, approvals.composioSlug, approvals.browser);
  return verdict !== null && (verdict.unknown.length > 0 || verdict.classes.some((c) => approvals.listed.has(c)));
}

/** Each class in the person's words (the company layer's own, @bash/shared CATEGORY_WORDS, mirrored: the engine cannot import it). */
const CLASS_WORDS: Readonly<Record<ApprovalClass, string>> = {
  payment: 'making a payment',
  booking: 'making a booking',
  cancellation: 'cancelling a booking or an order',
  send_email: 'sending an email',
  publish: 'publishing anything',
  delete: 'deleting anything',
};

/** The held card's why-line: the listed classes it is in, or, when it is in none, that no one has reviewed the action. */
export function approvalReasonLine(listedClasses: readonly ApprovalClass[]): string {
  const words = listedClasses.map((c) => CLASS_WORDS[c]);
  if (words.length === 0) return 'The company asks before any app action it has not reviewed.';
  const joined = words.length === 1 ? words[0] : `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`;
  return `The company asks before ${joined}.`;
}
